import RGL, { WidthProvider } from 'react-grid-layout';
import React, { useState, useEffect, useRef } from 'react';
import { string, object, func, array, bool, number } from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import AddModalComponent from './SupportingComponents/AddModal/AddModal';
import DropdownViews from 'ps-components/Dropdown/DropdownViews';
import { getViews, saveDashboard, getDashboard, upsertView, deleteView, copyView } from './LandingDashboardActions';
import { showSlideout, hideSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { SLIDEOUTS as MP_SLIDEOUTS } from '../Slideout/Slideouts';
import { PAGE } from '../../lib/pageCodeDefaults';

import Card from './Card/Card';
import CardMappings from './CardMappings';
import { SLIDEOUTS } from '../Slideout/Slideouts';

import './LandingDashboardPage.scss';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

LandingDashboardPage.propTypes = {
  userName: string,
  launchDarklyFeatureFlags: object,
  getViews: func,
  saveDashboard: func,
  getDashboard: func,
  upsertView: func,
  views: array,
  showSlideout: func,
  currentLayout: object,
  permissions: object,
  subsite: string,
  masterAccountId: string,
  deleteView: func,
  copyView: func,
  currentView: object,
  groupAdminCanConfigureViews: bool,
  regularUserCanConfigureViews: bool,
  groupId: number,
  organizationId: number,
  history: object
};

const ReactGridLayout = WidthProvider(RGL);

function LandingDashboardPage({
  userName,
  showSlideout,
  getViews,
  saveDashboard,
  history,
  getDashboard,
  upsertView,
  currentLayout,
  views,
  launchDarklyFeatureFlags,
  permissions,
  subsite,
  masterAccountId,
  deleteView,
  copyView,
  currentView,
  groupAdminCanConfigureViews,
  regularUserCanConfigureViews,
  groupId,
  organizationId
}) {
  const [currentBoard, setCurrentBoard] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [toBeAdded, setToBeAdded] = useState([]);
  const addModalRef = useRef(null);
  const addViewModal = useRef(null);

  useEffect(() => {
    getViews(masterAccountId);
    getDashboard(masterAccountId);
    if (history.location.hash) history.push(history.location.pathname);
  }, []);

  useEffect(() => {
    setCurrentBoard(currentLayout);
  }, [currentLayout]);

  useEffect(() => {
    if (
      typeof launchDarklyFeatureFlags['configurable-dashboard'] === 'boolean' &&
      !launchDarklyFeatureFlags['configurable-dashboard']
    )
      history.push('/');
  }, [launchDarklyFeatureFlags]);

  useEffect(() => {
    toBeAdded.forEach(cardId => onAdd(cardId));
  }, [toBeAdded]);

  function onLayoutChange(layout) {
    setCurrentBoard([...layout]);
  }

  function startEditing() {
    setIsEditing(true);
  }

  function cancelEditing() {
    setIsEditing(false);
    setCurrentBoard(currentLayout);
  }

  function saveEditing() {
    setIsEditing(false);
    saveDashboard(currentView.view_pk, currentBoard);
  }

  function onAdd(cardId) {
    const foundCard = CardMappings[cardId];

    const newCard = {
      i: cardId.toString(),
      x: 0,
      y: 0,
      h: foundCard.minH ? foundCard.minH : 1,
      w: foundCard.minW ? foundCard.minW : 1,
      minW: foundCard.minW,
      maxW: foundCard.maxW,
      minH: foundCard.minH,
      maxH: foundCard.maxH
    };

    if (!currentBoard.find(card => card.i == newCard.i)) {
      currentBoard.unshift(newCard);
      setCurrentBoard([...currentBoard]);
    }
  }

  function onDelete(id) {
    const newCurrentBoard = currentBoard.filter(l => l.i != id);
    setCurrentBoard(newCurrentBoard);
  }

  function canEditView(view) {
    const {
      globalAdmin,
      groupAdmin,
      readOnlyUser,
      reportViewUser,
      restrictedUser,
      siteAdmin,
      superAdmin
    } = permissions;
    const scope = view.scope;

    // read-only users can edit and create their own personal views
    if (readOnlyUser) return scope === 'personal';

    if (reportViewUser || restrictedUser) return false;

    const groupOrAbove = groupAdmin || siteAdmin || globalAdmin || superAdmin;
    const siteOrAbove = siteAdmin || globalAdmin || superAdmin;
    const globalAdminOrAbove = globalAdmin || superAdmin;
    const isGroupSameAsOrg = groupId === organizationId;

    if (!groupOrAbove && !regularUserCanConfigureViews) {
      return false;
    }

    if (!siteOrAbove && groupOrAbove && !groupAdminCanConfigureViews) {
      return false;
    }

    if (scope === 'personal') return true;
    if (scope === 'group' && groupOrAbove && !isGroupSameAsOrg) return true;
    if (scope === 'site' && siteOrAbove) return true;
    if (scope === 'global' && globalAdminOrAbove && subsite === 'marketpaymaster') return true;

    return false;
  }

  function showAddWidgetsSlideout() {
    showSlideout(SLIDEOUTS.DASHBOARD_ADD_WIDGETS, {
      currentCards: Object.keys(CardMappings).filter(cardId => !currentBoard.find(card => card.i == cardId)),
      setToBeAdded,
      viewName: currentView.view_name
    });
  }

  function handleViewChange(viewName, view) {
    setIsEditing(false);
    getDashboard(masterAccountId, view);
  }

  function showModal(permissions, view, isDefaultView, isSelectedView) {
    if (addModalRef?.current && view?.view_pk)
      addModalRef?.current.setStates(
        view.view_pk,
        view.scope,
        view.view_name,
        view.view_description,
        !!isDefaultView,
        permissions,
        !canEditView(view)
      );
    else if (addModalRef?.current) addModalRef?.current.setStates(null, null, null, null, null, permissions, false);
    addViewModal?.current?.show();
  }

  function showEditSlideout() {
    showSlideout(MP_SLIDEOUTS.CONFIGURE_VIEW, {
      onRefresh: () => getViews(masterAccountId).then(() => getDashboard(masterAccountId)),
      page: PAGE.DASHBOARD,
      pageName: 'Dashboard',
      pageCode: 'dashboard_landing',
      onCreate: showModal,
      onDelete: deleteView,
      onCopy: copyView,
      onClickView: showModal,
      innerComponent: (
        <AddModalComponent
          ref={addModalRef}
          addViewModal={addViewModal}
          upsertView={upsertView}
          currentView={currentView}
          getViews={getViews}
          getDashboard={getDashboard}
          masterAccountId={masterAccountId}
        />
      )
    });
  }

  return (
    <div className="dashboard-grid__page-container">
      <div className="dashboard-grid__header">
        <div className="welcome-text">Welcome back {userName}</div>
        <div className="view-row__dropdown-container">
          {views && (
            <div className="dropdown-group">
              {currentView && (
                <Dropdown
                  className="dashboard-dropdown"
                  text={currentView.view_name}
                  customRenderComponent={DropdownViews}
                  customRenderProps={{
                    views,
                    isDashboard: true,
                    selectedViewPk: currentView.view_pk,
                    onEdit: showEditSlideout,
                    onSelectView: handleViewChange,
                    userRights: permissions
                  }}
                />
              )}
            </div>
          )}
        </div>
        <div className="static-buttons">
          {!isEditing && canEditView(currentView) && (
            <button title="Edit the current dashboard" className="pxl-btn" onClick={startEditing}>
              Edit Dashboard
            </button>
          )}
          {isEditing && (
            <button title="Open the add widgets slideout" className="pxl-btn" onClick={showAddWidgetsSlideout}>
              + Add widgets
            </button>
          )}
          {isEditing && (
            <button title="Cancel changes to the current dashboard" className="pxl-btn" onClick={cancelEditing}>
              Cancel
            </button>
          )}
          {isEditing && (
            <button title="Save the current dashboard" className="pxl-btn" onClick={saveEditing}>
              Save
            </button>
          )}

          {!isEditing && (
            <Link title="Price an adhoc job" className="pxl-btn" to="/market-analysis/jobs/adhoc/survey-library">
              Adhoc Pricing
            </Link>
          )}
          {!isEditing && (
            <Link title="Market price a job" className="pxl-btn pxl-btn--primary" to="/market-analysis/market-pricing">
              Market Price a Job
            </Link>
          )}
        </div>
      </div>

      <ReactGridLayout
        className="layout dashboard-grid__container"
        onLayoutChange={onLayoutChange}
        layout={currentBoard}
        compactType={'vertical'}
        cols={3}
        isResizable={isEditing}
        isDraggable={isEditing}
      >
        {currentBoard?.map(card => (
          <div className="dashboard-card__container" data-grid={card} key={card.i}>
            <Card
              card={card}
              title={CardMappings[card.i].title}
              onDelete={() => onDelete(`${card.i}`)}
              isEditing={isEditing}
            >
              {CardMappings[card.i].component}
            </Card>
          </div>
        ))}
      </ReactGridLayout>
    </div>
  );
}

const mapStateToProps = ({ session, landingDashboard }) => ({
  views: landingDashboard.MainDashboardReducer.views,
  currentLayout: landingDashboard.MainDashboardReducer.currentLayout,
  currentView: landingDashboard.MainDashboardReducer.currentView,
  viewSuccess: landingDashboard.MainDashboardReducer.viewSuccess,
  masterAccountId: session.organization.masterAccountId,
  userName: session.user.firstName,
  permissions: session.user.rights,
  subsite: session.organization.code,
  groupAdminCanConfigureViews: session.groupAdminCanConfigureViews,
  regularUserCanConfigureViews: session.regularUserCanConfigureViews,
  groupId: Number(session.group.id),
  organizationId: Number(session.organization.defaultGroup.id)
});

const actions = {
  showSlideout,
  hideSlideout,
  getViews,
  saveDashboard,
  getDashboard,
  upsertView,
  deleteView,
  copyView
};

export default withRouter(connect(mapStateToProps, actions)(LandingDashboardPage));
